import {
  IconBolt,
  IconExchange,
  IconFileText,
  IconPlus,
  IconSettings,
  IconSparkles,
  IconStack,
} from '@tabler/icons-react'
import classNames from 'classnames'
import { useListProjects, useTeamProjects } from 'hooks/projects'
import { Project } from 'lib/types/generated-types'
import { Button } from './Button'
import { useRouter } from 'next/router'
import { Tooltip } from 'react-tooltip'
import Link from 'next/link'
import { useProjectStore } from 'store/useProjectStore'
import { useEffect } from 'react'
import { useCreateChatbotMutation } from 'hooks/mutations/chatbots/useCreateChatbotMutation'
import { toast } from 'react-hot-toast'
import { useUserPlan } from 'hooks/useUserPlan'
import { useCredits } from 'hooks/usage'
import { useAuth } from 'util/auth'
import { useCreateGeneratedContent } from 'hooks/mutations/generated_content/useCreateGeneratedContent'
import { useTemplateFormStore } from 'store/useTemplateFormStore'
import { useSetupStore } from 'hooks/useSetupStore'

export const SidebarProjects = () => {
  const {
    data: myProjects,
    isLoading: myProjectsLoading,
    refetch: refetchMyProjects,
  } = useListProjects()
  const {
    data: teamProjects,
    isLoading: teamProjectsLoading,
    refetch: refetchTeamProjects,
  } = useTeamProjects()
  const { setActiveProjectId, activeProjectId } = useProjectStore()

  useEffect(() => {
    if (
      myProjects?.items &&
      myProjects?.items?.length > 0 &&
      !activeProjectId
    ) {
      setActiveProjectId(myProjects?.items[0]?.id)
    }
  }, [myProjects, activeProjectId, setActiveProjectId])

  const { project } = useSetupStore()

  useEffect(() => {
    if (project && (!myProjects?.totalCount || !teamProjects?.totalCount)) {
      refetchMyProjects()
      refetchTeamProjects()
    }
  }, [
    project,
    myProjects,
    teamProjects,
    refetchMyProjects,
    refetchTeamProjects,
  ])

  if (myProjectsLoading || teamProjectsLoading)
    return (
      <div className="flex flex-col">
        <SidebarProjectsSkeleton />
        <SidebarProjectsSkeleton />
      </div>
    )

  return (
    <div className="flex flex-col pl-4 pr-2">
      <ProjectsSection
        title="My Projects"
        hasNewProjectButton
        projects={myProjects?.items || []}
        emptyMessage=""
      />
      {teamProjects?.items && teamProjects?.items?.length > 0 && (
        <ProjectsSection
          title="Projects Shared with Me"
          projects={teamProjects?.items || []}
          emptyMessage="No shared projects available."
        />
      )}
    </div>
  )
}

const ProjectsSection = ({
  title,
  projects,
  emptyMessage,
  hasNewProjectButton = false,
}: {
  title: string
  projects: Project[]
  emptyMessage: string
  hasNewProjectButton?: boolean
}) => {
  const { setShowNewProjectModal } = useProjectStore()
  const { maxProjects, remainingProjects } = useUserPlan()

  return (
    <div className="mt-6">
      <div className="mb-2 flex items-center justify-between">
        <h2 className="overflow-hidden text-ellipsis whitespace-nowrap text-xs font-bold text-gray-500">
          {title}
        </h2>
        {hasNewProjectButton && (
          <Button
            onClick={() => {
              if (remainingProjects <= 0) {
                toast.error(
                  `You have reached the maximum number of projects (${maxProjects}) for your plan. Please upgrade your plan to create more projects.`
                )
              } else {
                setShowNewProjectModal(true)
              }
            }}
            variant="outline"
            color="white"
            icon={IconPlus}
            size="xs"
            title="Create a new project"
          >
            Project
          </Button>
        )}
      </div>
      <div className="flex flex-col gap-y-2 text-sm">
        {projects.length > 0 ? (
          projects.map((project, index) => (
            <div className="flex flex-col" key={project.id}>
              <SidebarDropdown project={project} />
            </div>
          ))
        ) : (
          <p className="text-xs text-gray-500">{emptyMessage}</p>
        )}
      </div>
    </div>
  )
}

const SidebarDropdown = ({ project }: { project: Project }) => {
  const router = useRouter()
  const project_id = router.query.project_id
  const { activeProjectId, setActiveProjectId, setShowUploadModal } =
    useProjectStore()

  useEffect(() => {
    if (project_id && parseInt(project_id as string) !== activeProjectId) {
      setActiveProjectId(parseInt(project_id as string))
    }
  }, [project_id])

  const handleUpload = () => {
    if (!router.pathname.includes('/projects/[project_id]')) {
      router.push(`/projects/${project.id}`)
    }
    setShowUploadModal(true)
  }

  const { mutateAsync: createChatbot } = useCreateChatbotMutation()

  const auth = useAuth()
  const { data: credits } = useCredits({
    projectId: project.id,
    userId: auth.user ? auth.user.id : '',
  })

  const handleCreateChatbot = async () => {
    if ((credits?.remainingChatbots ?? 0) <= 0) {
      toast.error(
        `You have reached the maximum number of chatbots (${credits?.maxChatbots}) for this project. Please upgrade your plan to create more chatbots in this project.`
      )
      return
    }
    if (!router.pathname.includes('/projects/[project_id]')) {
      router.push(`/projects/${project.id}`)
    }
    setTimeout(async () => {
      const { data, error } = await createChatbot(project.id)
      if (error) {
        toast.error('Failed to create chatbot')
      } else {
        router.push(`/projects/${project.id}/chatbots/${data.id}`)
      }
    }, 0)
  }

  return (
    <div className="flex flex-col">
      <div
        className={classNames(
          'relative -mx-2 flex w-full cursor-pointer items-center rounded py-1 pl-2 text-left transition-all duration-300',
          activeProjectId === project.id
            ? 'bg-slate-600 text-white'
            : 'hover:bg-gray-100'
        )}
        onClick={async () => {
          setActiveProjectId(project.id)
          router.push(`/projects/${project.id}`)
        }}
      >
        <span className="flex w-full flex-grow items-center space-x-2">
          <IconStack
            className={classNames(
              'h-4 w-4',
              activeProjectId === project.id ? 'text-white' : 'text-gray-700'
            )}
          />
          <span className="overflow-hidden text-ellipsis whitespace-nowrap text-[13px] font-semibold">
            {project.name}
          </span>
        </span>
      </div>
      {activeProjectId === project.id && (
        <div className="mt-4 space-y-4 pl-2 text-xs transition-all duration-300">
          <DropdownItem
            label="Uploads"
            icon={IconBolt}
            onClick={() => router.push(`/projects/${project.id}`)}
            hasButton
            onButtonClick={handleUpload}
            buttonTooltip="Upload new content"
            iconClassName="text-toasty-blue-500"
            project_id={project.id}
          />
          <DropdownItem
            label="Multi-Chat"
            icon={IconExchange}
            onClick={() => router.push(`/projects/${project.id}/chatbots`)}
            hasButton
            onButtonClick={handleCreateChatbot}
            buttonTooltip="Create a new chat session"
            iconClassName="text-green-700"
            project_id={project.id}
          />
          <DropdownItem
            label="Long-Form"
            icon={IconFileText}
            onClick={() => router.push(`/projects/${project.id}/long-form`)}
            hasButton={true}
            onButtonClick={() => {}}
            buttonTooltip="Create a new long-form article"
            iconClassName="text-orange-700"
            project_id={project.id}
          />
          <DropdownItem
            label="Settings"
            icon={IconSettings}
            onClick={() => router.push(`/projects/${project.id}/settings`)}
            isSettings
            iconClassName="text-purple-800"
            project_id={project.id}
          />
        </div>
      )}
    </div>
  )
}

export const DropdownItem = ({
  label,
  icon: Icon,
  onClick,
  buttonTooltip,
  hasButton = false,
  onButtonClick,
  disabled = false,
  isSettings = false,
  project_id,
  iconClassName,
}: {
  label: string
  icon: any
  onClick?: () => void
  buttonTooltip?: string
  hasButton?: boolean
  onButtonClick?: () => void
  isSettings?: boolean
  project_id?: number
  iconClassName?: string
  disabled?: boolean
}) => {
  const { setActiveProjectId } = useProjectStore()
  const router = useRouter()
  const { mutateAsync: createGeneratedContent } = useCreateGeneratedContent()
  const { resetFormData } = useTemplateFormStore()

  const handleClick = () => {
    if (project_id) {
      setActiveProjectId(project_id)
    }
    onClick && onClick()
  }

  const handleButtonClick = async (e: React.MouseEvent) => {
    e.stopPropagation()
    if (label === 'Long-Form' && project_id) {
      try {
        resetFormData()
        const newContent = await createGeneratedContent({
          type: 'article',
          content: '',
          status: 'not_started',
          project_id: project_id,
        })
        router.push(`/projects/${project_id}/long-form/${newContent.id}`)
      } catch (error) {
        console.error('Error creating new article:', error)
        toast.error('Failed to create new article')
      }
    } else {
      onButtonClick && onButtonClick()
    }
  }

  // New function for child Links to prevent calling parent's onClick
  const handleChildClick = (
    e: React.MouseEvent<HTMLAnchorElement, MouseEvent>
  ) => {
    e.stopPropagation() // Prevents the event from bubbling up to the parent
    if (project_id) {
      setActiveProjectId(project_id)
    }
  }

  return (
    <div className="flex flex-col">
      <div className="flex h-4 w-28 items-center justify-between">
        <div
          className="flex cursor-pointer items-center gap-x-2"
          onClick={handleClick}
        >
          {Icon && (
            <Icon
              className={classNames('h-4 w-4 text-gray-500', iconClassName)}
            />
          )}
          <span className="text-gray-700">{label}</span>
        </div>

        {hasButton && (
          <>
            <div className="relative">
              <button
                onClick={handleButtonClick}
                data-tooltip-id={`${label}-${buttonTooltip}`}
                disabled={disabled}
                className="rounded-full border p-0.5 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
              >
                <IconPlus className="h-2.5 w-2.5" />
              </button>
              {buttonTooltip && (
                <Tooltip
                  id={`${label}-${buttonTooltip}`}
                  content={buttonTooltip}
                />
              )}
            </div>
          </>
        )}
      </div>
      {isSettings && project_id && (
        <div className="ml-2 mt-2 flex flex-col">
          <Link
            href={`/projects/${project_id}/prompts`}
            onClick={handleChildClick}
            className="relative flex h-6 items-center"
          >
            <span className="ml-4 text-gray-500">Prompt Packs</span>
            <span className="absolute left-0 top-0 h-full w-[1px] bg-gray-200" />
          </Link>
          {/* <Link
            href={`/projects/${project_id}/speakers`}
            onClick={handleChildClick}
            className="relative flex h-6 items-center"
          >
            <span className="ml-4 text-gray-500">Speakers</span>
            <span className="absolute left-0 top-0 h-full w-[1px] bg-gray-200" />
          </Link> */}
          <Link
            href={`/projects/${project_id}/access`}
            onClick={handleChildClick}
            className="relative flex h-6 items-center"
          >
            <span className="ml-4 text-gray-500">Access</span>
            <span className="absolute left-0 top-0 h-[65%] w-[1px] bg-gray-200" />
          </Link>
        </div>
      )}
    </div>
  )
}

const SidebarProjectsSkeleton = () => {
  return (
    <div className="flex animate-pulse flex-col pl-4 pr-2">
      <div className="mt-6">
        <div className="flex flex-col gap-y-2 text-sm">
          <div className="mt-2 flex flex-col">
            <div className="flex flex-col">
              <div className="relative ml-2 flex w-32 items-center rounded bg-slate-300 py-2.5 pl-2"></div>
              <div className="mt-4 space-y-4 pl-2">
                <div className="flex flex-col">
                  <div className="flex h-4 w-32 items-center justify-between gap-x-2">
                    <div className="flex items-center gap-x-2">
                      <div className="h-4 w-4 rounded bg-slate-300"></div>
                      <div className="h-4 w-28 rounded bg-slate-300"></div>
                    </div>
                  </div>
                </div>
                <div className="flex flex-col">
                  <div className="flex h-4 w-32 items-center justify-between gap-x-2">
                    <div className="flex items-center gap-x-2">
                      <div className="h-4 w-4 rounded bg-slate-300"></div>
                      <div className="h-4 w-28 rounded bg-slate-300"></div>
                    </div>
                  </div>
                </div>
                <div className="flex flex-col">
                  <div className="flex h-4 w-32 items-center justify-between gap-x-2">
                    <div className="flex items-center gap-x-2">
                      <div className="h-4 w-4 rounded bg-slate-300"></div>
                      <div className="h-4 w-28 rounded bg-slate-300"></div>
                    </div>
                  </div>
                  <div className="ml-2 mt-2 flex flex-col">
                    <div className="relative flex h-6 items-center">
                      <div className="ml-4 h-4 w-24 rounded bg-slate-300"></div>
                      <div className="absolute left-0 top-0 h-full w-[1px] bg-slate-200"></div>
                    </div>
                    <div className="relative flex h-6 items-center">
                      <div className="ml-4 h-4 w-24 rounded bg-slate-300"></div>
                      <div className="absolute left-0 top-0 h-[65%] w-[1px] bg-slate-200"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
